import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MultiSelect } from "@/components/ui/multi-select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { ColumnDef } from "@tanstack/react-table";
import {
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  AlertTriangle
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useDocumentTypes } from "@/hooks/useDocumentTypes";

// Internal structure of documents_list table
interface DocumentsListRow {
  id: string;
  name: string;
  description: string;
  documents: number[];
}

export default function DocumentsList() {
  const [lists, setLists] = useState<DocumentsListRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Bring in the dynamically sourced document types
  const { data: documentTypes = [], isLoading: isLoadingTypes } =
    useDocumentTypes();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [editingList, setEditingList] = useState<DocumentsListRow | null>(null);
  const [listToDelete, setListToDelete] = useState<DocumentsListRow | null>(
    null
  );

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    documents: [] as string[] // MultiSelect operates on strings
  });

  const { toast } = useToast();

  const fetchLists = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("documents_lists")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setLists(data || []);
    } catch (error) {
      console.error("Error fetching documents lists:", error);
      toast({
        title: "Fehler",
        description: "Dokumentenlisten konnten nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const openCreateDialog = () => {
    setEditingList(null);
    setFormData({
      name: "",
      description: "",
      documents: []
    });
    setDialogOpen(true);
  };

  const openEditDialog = (list: DocumentsListRow) => {
    setEditingList(list);
    setFormData({
      name: list.name,
      description: list.description || "",
      // Map postgres int array into string array for multi-select
      documents: (list.documents || []).map(String)
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (list: DocumentsListRow) => {
    setListToDelete(list);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      // Map multi-select values back to integer arrays for DB
      const dbPayload = {
        name: formData.name,
        description: formData.description,
        documents: formData.documents.map(Number)
      };

      if (editingList) {
        const { error } = await supabase
          .from("documents_lists")
          .update(dbPayload)
          .eq("id", editingList.id);

        if (error) throw error;

        toast({
          title: "Erfolg",
          description: "Dokumentenliste wurde aktualisiert."
        });
      } else {
        const { error } = await supabase
          .from("documents_lists")
          .insert([dbPayload]);

        if (error) throw error;

        toast({
          title: "Erfolg",
          description: "Dokumentenliste wurde erstellt."
        });
      }

      setDialogOpen(false);
      fetchLists();
    } catch (error) {
      console.error("Error saving documents list:", error);
      toast({
        title: "Fehler",
        description: "Dokumentenliste konnte nicht gespeichert werden.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    if (!listToDelete) return;

    try {
      const { error } = await supabase
        .from("documents_lists")
        .delete()
        .eq("id", listToDelete.id);

      if (error) throw error;

      toast({
        title: "Erfolg",
        description: "Dokumentenliste wurde gelöscht."
      });

      setDeleteDialogOpen(false);
      setListToDelete(null);
      fetchLists();
    } catch (error) {
      console.error("Error deleting documents list:", error);
      toast({
        title: "Fehler",
        description: "Dokumentenliste konnte nicht gelöscht werden.",
        variant: "destructive"
      });
    }
  };

  const columns: ColumnDef<DocumentsListRow>[] = [
    {
      accessorKey: "name",
      header: "Name"
    },
    {
      accessorKey: "description",
      header: "Beschreibung",
      cell: ({ row }) => {
        const description = row.getValue("description") as string;
        return description ? (
          <div className="max-w-[300px] truncate" title={description}>
            {description}
          </div>
        ) : (
          "-"
        );
      }
    },
    {
      accessorKey: "documents",
      header: "Enthaltene Dokumente",
      cell: ({ row }) => {
        const docIds = (row.getValue("documents") as number[]) || [];
        if (docIds.length === 0)
          return <span className="text-gray-400">Keine</span>;

        // Take only first 3 documents to avoid breaking table layout
        const displayedDocs = docIds.slice(0, 3);
        const hiddenCount = docIds.length - 3;

        return (
          <div className="flex flex-wrap gap-1 items-center max-w-[400px]">
            {displayedDocs.map((id) => {
              const type = documentTypes.find((t) => t.id === id);
              if (!type) {
                return (
                  <Badge
                    variant="destructive"
                    key={id}
                    className="text-xs"
                    title={`Das Dokument mit der ID ${id} existiert nicht mehr.`}
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Fehlt (ID: {id})
                  </Badge>
                );
              }
              return (
                <Badge
                  variant="secondary"
                  key={id}
                  className="text-xs truncate max-w-[150px]"
                  title={type.name}
                >
                  {type.name}
                </Badge>
              );
            })}
            {hiddenCount > 0 && (
              <Badge
                variant="outline"
                className="text-xs text-muted-foreground"
              >
                + {hiddenCount} weitere
              </Badge>
            )}
          </div>
        );
      }
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => openEditDialog(row.original)}>
              <Pencil className="mr-2 h-4 w-4" />
              Bearbeiten
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={() => openDeleteDialog(row.original)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Löschen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  if (loading || isLoadingTypes) {
    return <div>Lade Dokumentenlisten...</div>;
  }

  // Map options using String values for ShadCN MultiSelect
  const multiSelectOptions = documentTypes.map((doc) => ({
    label: doc.name || "Unbenanntes Dokument",
    value: doc.id.toString()
  }));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Dokumentenliste</h2>
          <p className="text-gray-500">
            Erstellen und verwalten Sie Listen von erforderlichen Dokumenten.
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Neue Liste
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={lists}
        searchKey="name"
        filterPlaceholder="Liste suchen..."
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingList
                ? "Dokumentenliste bearbeiten"
                : "Neue Dokumentenliste"}
            </DialogTitle>
            <DialogDescription>
              {editingList
                ? "Bearbeite die Eigenschaften und enthaltenen Dokumente dieser Liste."
                : "Erstelle eine neue Liste, die Talenten als Aufgabe zugewiesen werden kann."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="z.B. Migration nach Bayern"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Beschreibe den Zweck dieser Dokumentenliste."
                className="min-h-[100px]"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="documents-select">
                Erforderliche Dokumente erfragen
              </Label>
              <MultiSelect
                options={multiSelectOptions}
                selectedValues={formData.documents}
                onValueChange={(vals) =>
                  setFormData({ ...formData, documents: vals })
                }
                placeholder="Dokumente auswählen"
                maxCount={10}
                modalPopover={true}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={!formData.name}>
              {editingList ? "Aktualisieren" : "Erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dokumentenliste löschen</DialogTitle>
            <DialogDescription>
              Sind Sie sicher, dass Sie "{listToDelete?.name}" löschen möchten?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
